class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    // Only the cart confirmation should be visible while it is open.
    window.SproutJoyShippingProgress?.suspend();
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent && !this.querySelector('[data-sj-shipping-notification]')) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      this.updateShippingProgress(cart);
      window.SproutJoyShippingProgress?.update(cart);
      if (!CartViewEvent || !cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  updateShippingProgress(cart) {
    const panel = this.querySelector('[data-sj-shipping-notification]');
    if (!panel) return;
    if (!cart || cart.currency !== 'EUR' || !cart.item_count) {
      panel.hidden = true;
      return;
    }

    const threshold = Number(panel.dataset.thresholdCents);
    const total = Number(cart.total_price || 0);
    if (!threshold || !Number.isFinite(total)) return;

    const remaining = Math.max(0, threshold - total);
    const percentage = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
    const text = remaining > 0
      ? `Nog ${new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(remaining / 100)} tot gratis verzending binnen Nederland`
      : 'Je hebt de grens voor gratis verzending binnen Nederland bereikt!';

    panel.querySelector('[data-sj-shipping-message]').textContent = text;
    panel.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(percentage));
    panel.querySelector('[data-sj-shipping-fill]').style.width = `${percentage}%`;
    panel.hidden = false;
  }

  close() {
    const wasOpen = this.notification.classList.contains('active');
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
    // The separate progress capsule appears only AFTER the confirmation is closed.
    if (wasOpen) window.SproutJoyShippingProgress?.reveal();
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
